import styled from "@emotion/styled";
import { layers } from "../../../constants/layers";
import { ThemeMode } from "../../../util/theme";
import { WIDTH_ENUM, gutter, WidthNames } from "../shared-variables";
import { getBackgroundColor, getBorderRadius, getBoxShadow, getColor } from "./modalTheme";

const maxWidthDimensions = `calc(100% - ${gutter * 2}px)`;

const maxHeightDimensions = (gutterHeight?: string | number): string => {
    if (typeof gutterHeight === "string") {
        const percent = parseInt(gutterHeight);

        return `${100 - percent * 2}%`;
    }

    const gutterHeightPx = typeof gutterHeight === "number" ? gutterHeight : gutter;

    return `calc(100% - ${gutterHeightPx * 2}px)`;
};

export const modalWidth = ({ widthName, widthValue }: PositionerProps) => {
    if (typeof widthValue === "number") {
        return `${widthValue}px`;
    }

    return widthName ? `${WIDTH_ENUM.widths[widthName]}px` : widthValue || "auto";
};

export const modalHeight = ({ heightValue }: { heightValue?: string | number }) => {
    if (typeof heightValue === "number") {
        return `${heightValue}px`;
    }
    return heightValue || "auto";
};

const positionerAbsoluteTop = (gutterHeight?: string | number): string => {
    if (typeof gutterHeight === "string") {
        return gutterHeight;
    }

    const gutterHeightPx = typeof gutterHeight === "number" ? gutterHeight : gutter;

    return `${gutterHeightPx}px`;
};

export const FillScreen = styled("div")`
    height: 100vh;
    left: 0;
    overflow-y: auto;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: ${layers.modal};
    -webkit-overflow-scrolling: touch;
`;

interface PositionerProps {
    gutterHeight?: number | string;
    widthName?: WidthNames;
    widthValue?: string | number;
}

export const PositionerAbsolute = styled("div")<Pick<PositionerProps, "widthValue" | "gutterHeight">>`
    position: absolute;
    top: ${({ gutterHeight }) => positionerAbsoluteTop(gutterHeight)};
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    height: ${({ gutterHeight }) => maxHeightDimensions(gutterHeight)};
    width: ${({ widthValue }) => widthValue};
    max-width: ${maxWidthDimensions};
    margin-left: auto;
    margin-right: auto;
    z-index: ${layers.modal};
    pointer-events: none;

    @media (min-width: 320px) and (max-width: 480px) {
        position: fixed;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        max-width: 100%;
    }
`;

export interface ModalContainerProps {
    themeMode: ThemeMode;
    heightValue?: string | number;
}

export const StyledModalContainer = styled("div")<ModalContainerProps>`
    background-color: ${getBackgroundColor};
    border-radius: ${getBorderRadius};
    box-shadow: ${getBoxShadow};
    color: ${getColor};
    display: flex;
    flex-direction: column;
    height: ${(props: ModalContainerProps) => modalHeight({ heightValue: props.heightValue })};
    max-height: 100%;

    /* the parent (PositionerAbsolute) turns off pointer events, so we need to turn it back on */
    pointer-events: auto;

    @media (min-width: 320px) and (max-width: 480px) {
        height: 100%;
        max-height: 100%;
        border-radius: 0;
    }
`;

PositionerAbsolute.displayName = "StyledPositionerAbsolute";
StyledModalContainer.displayName = "StyledModalContainer";
FillScreen.displayName = "StyledFillScreen";
